import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import Tooltip from "@mui/material/Tooltip";
import { checkViewerRole } from "common/checkers/viewerChecker";
import styles from "./projectCompanies.module.scss";
import { Button, BtnType } from "../../../primitives";
import { useModelPopup } from "../../../../common/hooks";
import NewProjectModal from "../../../../common/components/newProjectModal";
import { companyGetProjectTasks } from "../company.action";
import { useWindowSize } from "../../../../common/hooks/useWindowSize";
import MobileProjectCompanyRow from "./mobileProjectCompanyRow.component";
import DesktopProjectCompanyRow from "./desktopProjectCompanyRow.component";
import FordNewProjectModal from "../../../../common/components/fordNewProjectModal";

const ProjectCompanies = ({
  isPatientOrganization,
  company,
  usedInCompany,
  isAdmin,
}) => {
  const {
    companiesReducer: {
      companyReducer: {
        selectedCompany,
        companyProjectTasks,
        companyIsDownloading,
        shouldUpdateProjectTasks,
      },
    },
    councilReducer: { selectedCouncil },
  } = useSelector((state) => state);
  const { isMobile } = useWindowSize();
  const history = useHistory();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [blockFunctionality, setBlockFunctionality] = useState(false);

  useEffect(() => {
    if (checkViewerRole()) {
      setBlockFunctionality(true);
    }
  }, []);

  const popup = useModelPopup();

  const handleProjectClick = (e, task) => {
    if (task.current_user_has_access) {
      history.push(`/projects/${task.project?.id}`);
    } else {
      enqueueSnackbar("You don't have access to this project.", {
        variant: "error",
      });
    }
  };

  const handleCreateNewProject = () => {
    if (selectedCouncil?.name === "Ford") {
      popup.show({
        title: "Create Project",
        height: "80%",
        component: (
          <FordNewProjectModal data={selectedCompany} instance="company" />
        ),
      });

      return;
    }

    popup.show({
      title: "Create Project",
      component: (
        <NewProjectModal
          isPatientOrganization={isPatientOrganization}
          mode="modal"
          hideGskFromOptions={
            isPatientOrganization &&
            window.location.pathname.includes("companies")
          }
        />
      ),
    });
  };

  const redirectToProfile = (e, id) => {
    history.push(`/people/internal/${id}`);
  };

  useEffect(() => {
    if (isPatientOrganization && company) {
      if (!company?.id) {
        return;
      }

      dispatch(
        companyGetProjectTasks({
          company_id: company.id,
          enqueueSnackbar,
          isPatientOrganization: true,
        })
      );
      return;
    }

    if (selectedCompany) {
      dispatch(
        companyGetProjectTasks({
          company_id: selectedCompany.id,
          enqueueSnackbar,
        })
      );
    }
  }, [selectedCompany, isPatientOrganization, company]);

  useEffect(() => {
    if (shouldUpdateProjectTasks) {
      if (isPatientOrganization && company) {
        if (!company?.id) {
          return;
        }

        dispatch(
          companyGetProjectTasks({
            company_id: company.id,
            enqueueSnackbar,
            isPatientOrganization: true,
          })
        );
        return;
      }

      if (selectedCompany) {
        dispatch(
          companyGetProjectTasks({
            company_id: selectedCompany.id,
            enqueueSnackbar,
          })
        );
      }
    }
  }, [shouldUpdateProjectTasks]);

  return (
    <section className={`${companyIsDownloading ? "px-4" : ""}`}>
      <div className={styles.contentWrp}>
        <div className={styles.headerWrp}>
          <span className={styles.headerText}>
            {isPatientOrganization
              ? "Organization Mappings"
              : "Company Projects"}
          </span>
          {selectedCouncil?.name === "Kyndryl" && usedInCompany ? (
            isAdmin ? (
              <Button
                className={styles.newTask}
                btn={BtnType.FRAME_LESS}
                icon="icn-add"
                onClick={handleCreateNewProject}
              >
                {isPatientOrganization ? "NEW MAPPING" : "New project"}
              </Button>
            ) : null
          ) : !blockFunctionality ? (
            <Button
              className={styles.newTask}
              btn={BtnType.FRAME_LESS}
              icon="icn-add"
              onClick={handleCreateNewProject}
            >
              {isPatientOrganization ? "NEW MAPPING" : "New project"}
            </Button>
          ) : (
            <Tooltip
              title="You have View-Only access. To create a Project, please ask your Admin to upgrade your account to Standard access."
              placement="top"
            >
              <div className={styles.newTask}>
                <Button
                  className={styles.newTask}
                  btn={BtnType.DISABLED}
                  icon="icn-add"
                >
                  {isPatientOrganization ? "NEW MAPPING" : "New project"}
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
        <div className={styles.container}>
          <div className={styles.grid}>
            {companyProjectTasks?.length && !isMobile ? (
              <div className={styles.gridHeader}>
                <span className={styles.company}>
                  {" "}
                  {isPatientOrganization ? "mAPPING" : "project"}
                </span>
                <span className={styles.stage}>Stage</span>
                <span className={styles.company}>Owner</span>
                <span className={styles.company}>Access</span>
                <span className={styles.task}>Next Task</span>
                <span className={styles.assigned}>Assigned</span>
                <span className={styles.due}>Due</span>
                <span className={styles.due}>Status</span>
              </div>
            ) : null}
            <div className={styles.gridBody}>
              {companyProjectTasks?.length ? (
                companyProjectTasks.map((project) =>
                  isMobile ? (
                    <MobileProjectCompanyRow
                      key={project.id}
                      project={project}
                      handleProjectClick={handleProjectClick}
                      redirectToProfile={redirectToProfile}
                    />
                  ) : (
                    <DesktopProjectCompanyRow
                      key={project.id}
                      project={project}
                      handleProjectClick={handleProjectClick}
                      redirectToProfile={redirectToProfile}
                    />
                  )
                )
              ) : (
                <div>
                  {!companyProjectTasks?.length ? (
                    <div className="text-center pb-5">
                      {isPatientOrganization
                        ? "No mappings yet."
                        : " No projects yet."}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectCompanies;
